/**
 * AiChatInput — Auto-growing textarea with Send/Cancel buttons and @-mention support.
 * Supports dot-chaining for column suggestions and context-aware @ popup.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Send, Square } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import type { DatabaseObject } from '../../database-objects/models/databaseObjectTypes';
import type { ColumnInfo } from '../../database-objects/services/columnsService';
import { getColumns } from '../../database-objects/services/columnsService';
import { useAtMention } from '../hooks/useAtMention';
import { AiChatMentionPopup } from './AiChatMentionPopup';

type AiChatInputProps = {
  onSend: (message: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  disabled?: boolean;
  databaseObjects?: DatabaseObject[];
  connectionId?: string;
  database?: string;
  engine?: string;
};

export function AiChatInput({ onSend, onCancel, isLoading, disabled, databaseObjects, connectionId, database, engine }: AiChatInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mention = useAtMention(databaseObjects);

  // Track the last mentioned table/view for context-aware @ popup
  const lastMentionedObjectRef = useRef<{ schema: string; name: string; type: string } | null>(null);

  // Column cache: keyed by "schema.name"
  const columnCacheRef = useRef<Map<string, ColumnInfo[]>>(new Map());

  // Auto-focus textarea when sidebar opens (component mounts)
  useEffect(() => {
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  /** Fetch columns (with cache). */
  const fetchColumns = useCallback(async (schema: string, name: string): Promise<ColumnInfo[]> => {
    const key = `${schema}.${name}`;
    const cached = columnCacheRef.current.get(key);
    if (cached) return cached;

    if (!connectionId || !database) return [];

    try {
      const result = await getColumns(connectionId, database, schema, name, engine);
      if (result.columns.length > 0) {
        columnCacheRef.current.set(key, result.columns);
      }
      return result.columns;
    } catch {
      return [];
    }
  }, [connectionId, database, engine]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setValue('');
    // Reset textarea height
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [value, isLoading, onSend]);

  /** Insert a database object mention (table/view/procedure/function). */
  const insertObjectMention = useCallback((obj: DatabaseObject) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const before = value.slice(0, mention.state.triggerIndex);
    const after = value.slice(ta.selectionStart);
    const inserted = `@${obj.schema}.${obj.name}`;
    // No trailing space — next character determines behavior (dot = columns, other = auto-space)
    const next = before + inserted + after;
    setValue(next);
    mention.close();

    // Track last mentioned table/view for context-aware @ popup
    if (obj.type === 'table' || obj.type === 'view') {
      lastMentionedObjectRef.current = { schema: obj.schema, name: obj.name, type: obj.type };
      // Pre-fetch columns
      void fetchColumns(obj.schema, obj.name);
    }

    // Move cursor right after the inserted mention
    requestAnimationFrame(() => {
      const pos = before.length + inserted.length;
      ta.setSelectionRange(pos, pos);
      ta.focus();
    });
  }, [value, mention, fetchColumns]);

  /** Insert a column mention. */
  const insertColumnMention = useCallback((col: ColumnInfo) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const before = value.slice(0, mention.state.triggerIndex);
    const after = value.slice(ta.selectionStart);

    const parentName = mention.state.parentObjectName;
    const inserted = parentName ? `@${parentName}.${col.name}` : `@${col.name}`;
    const next = before + inserted + ' ' + after;
    setValue(next);
    mention.close();

    requestAnimationFrame(() => {
      const pos = before.length + inserted.length + 1;
      ta.setSelectionRange(pos, pos);
      ta.focus();
    });
  }, [value, mention]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    // Auto-grow (max 5 lines ~= 120px)
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;

    // @-mention detection
    const cursorPos = ta.selectionStart;
    const textBeforeCursor = newValue.slice(0, cursorPos);

    if (mention.state.isOpen) {
      // Check for dot-chain trigger: @schema.name. (dot at end)
      if (mention.state.mode === 'objects' || mention.state.mode === 'mixed') {
        const dotChainMatch = textBeforeCursor.match(/@(\w+)\.(\w+)\.$/);
        if (dotChainMatch) {
          const schema = dotChainMatch[1];
          const name = dotChainMatch[2];
          // Check if this is a known table/view
          const obj = databaseObjects?.find(
            (o) => o.schema === schema && o.name === name && (o.type === 'table' || o.type === 'view')
          );
          if (obj) {
            const cacheKey = `${schema}.${name}`;
            const cached = columnCacheRef.current.get(cacheKey);
            lastMentionedObjectRef.current = { schema, name, type: obj.type };
            if (cached && cached.length > 0) {
              // Cached — show immediately, no async gap
              mention.enterColumnMode(mention.state.triggerIndex, cached, `${schema}.${name}`);
            } else {
              // Not cached — enter column mode with loading state, fetch async
              mention.enterColumnMode(mention.state.triggerIndex, [], `${schema}.${name}`);
              mention.setColumnsLoading(true);
              void (async () => {
                const columns = await fetchColumns(schema, name);
                if (columns.length > 0) {
                  mention.updateColumns(columns);
                } else {
                  mention.close();
                }
              })();
            }
            return;
          }
        }
      }

      // Update query or close if cursor moved before @
      const queryPart = textBeforeCursor.slice(mention.state.triggerIndex + 1);
      if (textBeforeCursor[mention.state.triggerIndex] !== '@') {
        mention.close();
      } else if (mention.state.mode === 'columns') {
        // In column mode, query is everything after the last dot
        // Check if dot was deleted (backspace): if queryPart has no second dot, exit column mode
        const dotCount = (queryPart.match(/\./g) || []).length;
        if (dotCount < 2) {
          // Dot was deleted — switch back to objects mode with remaining query
          mention.setMode('objects');
          mention.setQuery(queryPart);
          return;
        }
        const afterLastDot = queryPart.slice(queryPart.lastIndexOf('.') + 1);
        if (afterLastDot.includes(' ')) {
          mention.close();
        } else {
          mention.setQuery(afterLastDot);
        }
      } else if (queryPart.includes(' ')) {
        mention.close();
      } else {
        mention.setQuery(queryPart);
      }
    } else {
      // Not open — check for triggers

      // Dot-chain trigger: user just typed a dot after @schema.name
      const dotChainMatch = textBeforeCursor.match(/@(\w+)\.(\w+)\.$/);
      if (dotChainMatch) {
        const schema = dotChainMatch[1];
        const name = dotChainMatch[2];
        const obj = databaseObjects?.find(
          (o) => o.schema === schema && o.name === name && (o.type === 'table' || o.type === 'view')
        );
        if (obj) {
          const triggerIndex = textBeforeCursor.lastIndexOf(`@${schema}.${name}.`);
          const cacheKey = `${schema}.${name}`;
          const cached = columnCacheRef.current.get(cacheKey);
          lastMentionedObjectRef.current = { schema, name, type: obj.type };
          if (cached && cached.length > 0) {
            mention.enterColumnMode(triggerIndex, cached, `${schema}.${name}`);
          } else {
            mention.enterColumnMode(triggerIndex, [], `${schema}.${name}`);
            mention.setColumnsLoading(true);
            void (async () => {
              const columns = await fetchColumns(schema, name);
              if (columns.length > 0) {
                mention.updateColumns(columns);
              } else {
                mention.close();
              }
            })();
          }
          return;
        }
      }

      // Auto-insert space after @schema.name when typing a non-dot character
      const autoSpaceMatch = textBeforeCursor.match(/@(\w+)\.(\w+)([^\s.])$/);
      if (autoSpaceMatch) {
        const schema = autoSpaceMatch[1];
        const name = autoSpaceMatch[2];
        const trailingChar = autoSpaceMatch[3];
        const obj = databaseObjects?.find(
          (o) => o.schema === schema && o.name === name
        );
        if (obj) {
          // Insert space before the trailing character
          const matchStart = textBeforeCursor.lastIndexOf(`@${schema}.${name}${trailingChar}`);
          const insertionPoint = matchStart + `@${schema}.${name}`.length;
          const newText = newValue.slice(0, insertionPoint) + ' ' + newValue.slice(insertionPoint);
          setValue(newText);
          requestAnimationFrame(() => {
            ta.setSelectionRange(cursorPos + 1, cursorPos + 1);
          });
          return;
        }
      }

      // Check for new @ trigger
      const lastChar = textBeforeCursor[textBeforeCursor.length - 1];
      if (lastChar === '@' && databaseObjects && databaseObjects.length > 0) {
        // Context-aware: if last-mentioned object exists, show columns at top
        const lastObj = lastMentionedObjectRef.current;
        if (lastObj) {
          void (async () => {
            const columns = await fetchColumns(lastObj.schema, lastObj.name);
            if (columns.length > 0) {
              mention.openWithColumns(cursorPos - 1, columns, `${lastObj.schema}.${lastObj.name}`);
            } else {
              mention.open(cursorPos - 1);
            }
          })();
        } else {
          mention.open(cursorPos - 1);
        }
      }
    }
  }, [mention, databaseObjects, fetchColumns]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // @-mention navigation
    if (mention.state.isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        mention.moveSelection(1);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        mention.moveSelection(-1);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        // Check column selection first (for column or mixed mode)
        const col = mention.getSelectedColumn();
        if (col) {
          insertColumnMention(col);
          return;
        }
        const obj = mention.getSelectedObject();
        if (obj) insertObjectMention(obj);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        mention.close();
        return;
      }
    }

    // Enter sends, Shift+Enter adds newline
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [mention, insertObjectMention, insertColumnMention, handleSend]);

  const popupPosition = { top: 0, left: 0 };

  return (
    <div className="relative border-t border-border px-3 py-2" data-testid="ai-chat-input">
      {mention.state.isOpen && (
        <AiChatMentionPopup
          objects={mention.filteredObjects}
          columns={mention.filteredColumns}
          mode={mention.state.mode}
          parentObjectName={mention.state.parentObjectName}
          selectedIndex={mention.state.selectedIndex}
          hasMore={mention.hasMore}
          remainingCount={mention.remainingCount}
          columnsLoading={mention.state.columnsLoading}
          onSelectObject={insertObjectMention}
          onSelectColumn={insertColumnMention}
          position={popupPosition}
        />
      )}

      <div className="flex items-end gap-1.5">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Delay close so click on popup item can fire first
            setTimeout(() => mention.close(), 150);
          }}
          placeholder="Ask Claude about your SQL..."
          disabled={disabled}
          rows={1}
          className="flex-1 resize-none bg-transparent text-sm outline-none placeholder:text-muted-foreground min-h-[32px] py-1.5"
          data-testid="ai-chat-textarea"
        />

        {isLoading ? (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            onClick={onCancel}
            data-testid="ai-chat-cancel-button"
          >
            <Square className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            onClick={handleSend}
            disabled={!value.trim() || disabled}
            data-testid="ai-chat-send-button"
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
